<template>
  <div class="progress-flashcard-component-container">
    <page-header :breadcrumbs="breadcrumbs">
      <template #topbar>
        <h1>{{ title }}</h1>
        <improve-question :question="currentFlashcard" />
      </template>
    </page-header>
    <cert-attachment
      :flashcard="currentFlashcard"
    />
    <flashcard
      v-if="currentFlashcard && currentFlashcardState"
      :answered="true"
      :answer-ids="answerIds"
      :correct="currentFlashcardState.correct"
      :flashcard="currentFlashcard"
      :flashcard-state="currentFlashcardState"
      :category="currentFlashcardCategory"
      :raw-answer-data="rawAnswerData"
      @set-answer-ids="setAnswerIds"
      @set-raw-answer-data="setRawAnswerData"
    />
    <page-footer>
      <router-link
        v-if="previousFlashcardId"
        :to="previousRoute"
        class="previous"
      >
        <icon
          name="arrow"
          variant="a8aab7"
        />
        Previous
      </router-link>
      <router-link
        v-if="nextFlashcardId"
        :to="nextRoute"
        class="next"
      >
        Next
        <icon
          name="arrow"
          variant="a8aab7"
        />
      </router-link>
      <div class="progress">
        <div class="counter">
          {{ currentStateIndex + 1 }}/{{ states.length }}
        </div>
        <pure-progress-bar
          :answered="currentStateIndex + 1"
          :total="states.length"
        />
      </div>
      <router-link
        :to="backRoute"
        class="back"
      >
        <icon
          class="list-icon"
          name="hamburger"
          variant="a8aab7"
        />Back To List
      </router-link>
    </page-footer>
  </div>
</template>

<script>
import CertAttachment from '@components/certAttachment';
import Flashcard from '@components/flashcards/flashcard';
import Icon from '@components/singletons/icon';
import ImproveQuestion from '@components/singletons/improveQuestion';
import PageHeader from '@components/singletons/pageHeader';
import PageFooter from '@components/singletons/pageFooter';
import PureProgressBar from '@/components/progress/PureProgressBar';

import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';

export default {
  components: {
    CertAttachment, Flashcard, ImproveQuestion, PageFooter, PageHeader, PureProgressBar, Icon,
  },
  mixins: [FlashcardViewMixin, GlobalMixin],
  computed: {
    currentCertification() {
      let userCertification = this.$store.getters['userCertifications/userCertifications'][this.currentResource.resourceId];
      if (userCertification) {
        return this.$store.getters['certifications/certifications'][userCertification.certificationId];
      }
    },
    currentCertificationCategory() {
      if (this.currentCertification) {
        return this.$store.getters['categories/categories'][this.currentCertification.categoryId];
      }
    },
    currentFlashcard() {
      return this.$store.getters['flashcards/flashcards'][this.$route.params.flashcardId];
    },
    currentFlashcardCategory() {
      if (this.currentFlashcardState) {
        return this.$store.getters['categories/categories'][this.currentFlashcardState.categoryId];
      }
    },
    currentFlashcardState() {
      return this.states.find(fs => fs.flashcardId == this.$route.params.flashcardId);
    },
    breadcrumbs() {
      if (this.currentCertification) {
        return [
          {
            name: 'Certification Progress',
            route: {
              name: 'userCertifications#progress',
              query: this.currentQuery,
            },
          },
          {
            name: 'Review',
            route: {},
          },
        ];
      } else {
        return [];
      }
    },
    title() {
      if (this.currentCertificationCategory) {
        return this.currentCertificationCategory.name;
      }
    },
    nextFlashcardId() {
      const index = this.currentStateIndex + 1;
      if (Number.isInteger(index) && index < this.states.length) {
        return this.states[index].flashcardId;
      }
    },
    previousFlashcardId() {
      const index = this.currentStateIndex - 1;
      if (Number.isInteger(index) && index >= 0) {
        return this.states[index].flashcardId;
      }
    },
    nextRoute() {
      return { name: 'userCertifications#progress#flashcard', params: { flashcardId: this.nextFlashcardId }, query: this.currentQuery };
    },
    previousRoute() {
      return { name: 'userCertifications#progress#flashcard', params: { flashcardId: this.previousFlashcardId }, query: this.currentQuery };
    },
    backRoute() {
      return { name: 'userCertifications#progress', query: this.currentQuery };
    },
    currentStateIndex() {
      if (this.currentFlashcardState) {
        return this.states.findIndex(s => s.flashcardId == this.currentFlashcardState.flashcardId);
      }
    },
    states() {
      if (this.currentCertificationCategory) {
        // flashcard state category could be on certification root category or descendant of that category
        const categoryIds = [this.currentCertificationCategory.id, ...this.currentCertificationCategory.descendants];
        let states = this.$store.getters['flashcardStates/flashcardStateList'].filter(fs => categoryIds.includes(fs.categoryId));
        return states.sort((a, b) => b.mobileCreatedAt - a.mobileCreatedAt);
      } else {
        return [];
      }
    },
  },
  created() {
    if (!this.currentFlashcardState) {
      this.$store.dispatch('flashcardStates/fetchStates', ['flashcards']).then(() => {
        if (!this.currentFlashcardState) {
          this.$router.go(-1);
        }
      });
    }
    if (!this.currentFlashcard) {
      this.$store.dispatch('flashcards/fetchFlashcards', { categoryId: this.currentCertification.categoryId });
    }
  },
};
</script>

<style lang='scss' scoped>
  .progress-flashcard-component-container {
    @include flashcard-view;

    .page-footer-component-container {
      .progress {
        align-items: center;
        display: flex;
        flex: 1;

        .counter {
          color: #354052;
          font-size: rem-calc(12);
          font-weight: $semi-bold;
          margin-right: rem-calc(8);
        }
      }

      > a {
        @include hlt-btn-1;
        font-size: rem-calc(14);
        padding: rem-calc(6) rem-calc(12);

        &.back {
          margin-left: auto;

          @include breakpoint(small) {
            display: none;
          }
        }

        &.next {
          margin-right: rem-calc(12);

          img {
            margin: 0 0 0 rem-calc(7);
          }
        }

        &.previous {
          margin-right: rem-calc(12);

          img {
            transform: rotate(180deg);
          }
        }

        img {
          height: rem-calc(16);
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }

        .list-icon {
          height: auto;
          margin: 0 rem-calc(7) 0 0;
          width: rem-calc(16);
        }
      }
    }
  }
</style>
